import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Icon } from "../../components/ui/Icon";
import { getOrders, updateOrderStatus } from "./orders.api";
import type { Order, OrderStatus } from "./orders.types";

type Filter = "all" | OrderStatus;

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const done = status === "done";
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${done ? "border-[#2E8B57]/25 bg-[#2E8B57]/10 text-[#2E8B57]" : "border-[#CC9A3E]/25 bg-[#CC9A3E]/10 text-[#A16D12]"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${done ? "bg-[#2E8B57]" : "bg-[#CC9A3E]"}`} />
      {done ? "Terminée" : "À traiter"}
    </span>
  );
}

export function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOrders()
      .then((loadedOrders) => {
        setOrders(loadedOrders);
        setSelectedId(loadedOrders[0]?.id || null);
      })
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => ({
    pending: orders.filter((order) => order.status === "pending").length,
    done: orders.filter((order) => order.status === "done").length,
    total: orders.reduce((sum, order) => sum + order.total, 0),
  }), [orders]);
  const visibleOrders = filter === "all" ? orders : orders.filter((order) => order.status === filter);
  const selectedOrder = orders.find((order) => order.id === selectedId) || null;

  async function changeStatus(order: Order) {
    const nextStatus: OrderStatus = order.status === "done" ? "pending" : "done";
    setUpdatingId(order.id);
    setError(null);
    try {
      const updatedOrder = await updateOrderStatus(order.id, nextStatus);
      setOrders((currentOrders) => currentOrders.map((candidate) => candidate.id === updatedOrder.id ? updatedOrder : candidate));
    } catch (updateError: any) {
      setError(updateError.message);
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-[1180px] flex-1 flex-col gap-5 px-5 py-8 lg:px-8">
      <section className="grid gap-3 sm:grid-cols-3">
        <Card className="px-5 py-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-tertiary">À traiter</div>
          <div className="mt-2 font-display text-[32px] font-bold text-[#A16D12]">{counts.pending}</div>
        </Card>
        <Card className="px-5 py-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-tertiary">Terminées</div>
          <div className="mt-2 font-display text-[32px] font-bold text-[#2E8B57]">{counts.done}</div>
        </Card>
        <Card className="px-5 py-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-tertiary">Volume commandé</div>
          <div className="mt-2 font-display text-[32px] font-bold text-foreground">{counts.total.toFixed(2)} €</div>
        </Card>
      </section>

      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.85fr)]">
        <Card className="flex min-h-[480px] flex-col p-0">
          <div className="border-b border-border px-5 py-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="label mb-1">Suivi des commandes</div>
                <h2 className="font-display text-[25px] font-bold text-foreground">Flux de commandes</h2>
              </div>
              <div className="flex rounded-[14px] border border-border bg-surface-2/60 p-1">
                {(["all", "pending", "done"] as Filter[]).map((option) => {
                  const active = filter === option;
                  const label = option === "all" ? "Toutes" : option === "pending" ? "À traiter" : "Terminées";
                  return <button key={option} onClick={() => setFilter(option)} className={`min-h-[36px] rounded-[10px] px-3 text-[12px] font-bold transition-colors ${active ? "bg-surface text-foreground shadow-sm" : "text-tertiary"}`}>{label}</button>;
                })}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-3">
            {error && <div className="mb-3 rounded-[12px] border border-[#A8324A]/20 bg-[#A8324A]/8 px-3 py-2 text-sm text-[#A8324A]">{error}</div>}
            {loading ? <div className="p-6 text-sm text-secondary">Chargement des commandes...</div> : visibleOrders.length === 0 ? <div className="p-6 text-sm text-secondary">Aucune commande dans cette vue.</div> : (
              <div className="space-y-2">
                {visibleOrders.map((order) => {
                  const selected = selectedId === order.id;
                  return <button key={order.id} onClick={() => setSelectedId(order.id)} className={`w-full rounded-[14px] border p-4 text-left transition-colors ${selected ? "border-accent-border bg-accent/7" : "border-border bg-transparent hover:bg-surface-2/50"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="truncate text-[15px] font-bold text-foreground">{order.form.nom}</div>
                        <div className="mt-1 truncate text-[12px] text-secondary">{order.form.email} · {formatDate(order.createdAt)}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-[15px] font-bold text-foreground">{order.total.toFixed(2)} €</div>
                        <div className="mt-1 text-[11px] text-tertiary">{order.items.reduce((sum, item) => sum + item.quantity, 0)} bouteilles</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3"><StatusBadge status={order.status} /><span className="text-[11px] font-semibold text-tertiary">{order.source === "public-carte" ? "Carte publique" : "Depuis l'espace"}</span></div>
                  </button>;
                })}
              </div>
            )}
          </div>
        </Card>

        <Card className="min-h-[480px] p-5">
          {selectedOrder ? <>
            <div className="flex items-start justify-between gap-3"><div><div className="label mb-1">Commande sélectionnée</div><h2 className="font-display text-[25px] font-bold text-foreground">{selectedOrder.form.nom}</h2></div><StatusBadge status={selectedOrder.status} /></div>
            <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-y border-border py-4 text-sm"><div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-tertiary">Montant</div><div className="mt-1 font-bold text-foreground">{selectedOrder.total.toFixed(2)} €</div></div><div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-tertiary">Date souhaitée</div><div className="mt-1 font-semibold text-foreground">{selectedOrder.form.dateSouhaitee || "Non précisée"}</div></div><div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-tertiary">Téléphone</div><div className="mt-1 font-semibold text-foreground">{selectedOrder.form.tel}</div></div><div><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-tertiary">Société</div><div className="mt-1 font-semibold text-foreground">{selectedOrder.form.societe || "Particulier"}</div></div></div>
            <div className="mt-5"><div className="label">Livraison</div><div className="text-sm leading-6 text-secondary">{selectedOrder.form.adresse || "Adresse non précisée"}</div></div>
            <div className="mt-5"><div className="label">Bouteilles demandées</div><div className="space-y-2">{selectedOrder.items.map((item) => <div key={item.wineId} className="flex items-center justify-between border-b border-border pb-2 text-sm"><span className="font-mono text-[11px] text-secondary">Réf. {item.wineId}</span><span className="font-bold text-foreground">× {item.quantity}</span></div>)}</div></div>
            {selectedOrder.form.message && <div className="mt-5"><div className="label">Message</div><p className="text-sm leading-6 text-secondary">{selectedOrder.form.message}</p></div>}
            <Button onClick={() => changeStatus(selectedOrder)} disabled={updatingId === selectedOrder.id} variant={selectedOrder.status === "done" ? "secondary" : "accent"} className="mt-6 w-full">{updatingId === selectedOrder.id ? "Mise à jour..." : <><Icon name={selectedOrder.status === "done" ? "history" : "check"} size={16} />{selectedOrder.status === "done" ? "Remettre à traiter" : "Marquer comme terminée"}</>}</Button>
          </> : <div className="flex h-full min-h-[360px] flex-col items-center justify-center text-center"><Icon name="cart" size={28} className="text-tertiary" /><div className="mt-4 font-display text-[21px] font-bold text-foreground">Aucune commande</div><p className="mt-2 max-w-[240px] text-sm leading-6 text-secondary">Les commandes reçues depuis votre carte apparaîtront ici.</p></div>}
        </Card>
      </div>
    </div>
  );
}